/**
 * Widget Snapshot Store
 *
 * Last-known-good data per dashboard widget, independent of the React Query
 * dehydrated cache. Why a second layer exists: the query-cache persister
 * writes ONE localStorage blob for the entire cache (all-or-nothing under the
 * ~5MB quota — one oversized write silently kills persistence for every
 * widget), and widget query keys embed discovered telemetry keys, so a
 * persisted entry is often unfindable after the key-map resolves or changes.
 *
 * Snapshots instead are keyed by STABLE widget identity (widget name + scope
 * id), written one entry per widget, and REPLACED on every successful fetch —
 * the store never grows beyond widgets × properties. When ThingsBoard is slow
 * or down, widgets render the snapshot instead of a blank card.
 *
 * @module lib/widgetSnapshot
 */

const PREFIX = 'ZENTRIAN_WIDGET_SNAP:';

/** Snapshots older than this are dropped on read (matches query persistence maxAge). */
const MAX_AGE_MS = 24 * 60 * 60 * 1000;

/**
 * Per-entry size cap. A widget snapshot is the small render payload (current
 * values, short series) — anything bigger is a chart history that belongs in
 * the query cache, not here. Oversized writes are skipped, never truncated.
 */
const MAX_ENTRY_BYTES = 256 * 1024;

export interface WidgetSnapshot<T> {
  data: T;
  savedAt: number;
}

function storageKey(widget: string, scopeId: string): string {
  return `${PREFIX}${widget}:${scopeId}`;
}

function listSnapshotKeys(): string[] {
  const keys: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.startsWith(PREFIX)) keys.push(key);
  }
  return keys;
}

/**
 * Persist the latest successful widget data, replacing any previous snapshot
 * for the same widget + scope. Failures are non-fatal: on quota exhaustion the
 * oldest snapshots are evicted and the write retried; if it still fails, only
 * this one entry is lost — never the whole store.
 */
export function saveWidgetSnapshot(widget: string, scopeId: string, data: unknown): void {
  let serialized: string;
  try {
    serialized = JSON.stringify({ data, savedAt: Date.now() } satisfies WidgetSnapshot<unknown>);
  } catch {
    return; // non-serializable data — nothing to store
  }
  if (serialized.length > MAX_ENTRY_BYTES) return;

  const key = storageKey(widget, scopeId);
  for (let attempt = 0; attempt < 3; attempt++) {
    try {
      localStorage.setItem(key, serialized);
      return;
    } catch {
      if (!evictOldestSnapshot(key)) return;
    }
  }
}

/** Remove the oldest snapshot (excluding `keep`). Returns false when there is nothing left to evict. */
function evictOldestSnapshot(keep: string): boolean {
  let oldestKey: string | null = null;
  let oldestTs = Infinity;
  for (const key of listSnapshotKeys()) {
    if (key === keep) continue;
    try {
      const parsed = JSON.parse(localStorage.getItem(key) ?? '') as WidgetSnapshot<unknown>;
      if (typeof parsed.savedAt === 'number' && parsed.savedAt < oldestTs) {
        oldestTs = parsed.savedAt;
        oldestKey = key;
      }
    } catch {
      // unreadable entry — evict it first
      localStorage.removeItem(key);
      return true;
    }
  }
  if (!oldestKey) return false;
  localStorage.removeItem(oldestKey);
  return true;
}

/**
 * Read the last-known snapshot for a widget + scope.
 * Returns null (and self-cleans) when missing, unreadable, or older than 24h.
 */
export function loadWidgetSnapshot<T>(widget: string, scopeId: string): WidgetSnapshot<T> | null {
  const key = storageKey(widget, scopeId);
  const raw = localStorage.getItem(key);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as WidgetSnapshot<T>;
    if (typeof parsed.savedAt !== 'number' || parsed.data === undefined) throw new Error('malformed');
    if (Date.now() - parsed.savedAt > MAX_AGE_MS) {
      localStorage.removeItem(key);
      return null;
    }
    return parsed;
  } catch {
    localStorage.removeItem(key);
    return null;
  }
}

/** Remove all widget snapshots (used on logout alongside the query cache). */
export function clearWidgetSnapshots(): void {
  for (const key of listSnapshotKeys()) {
    localStorage.removeItem(key);
  }
}
